import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Paper,
    Tabs,
    Tab,
    Alert,
    Snackbar,
    CircularProgress
} from '@mui/material';
import { Settings } from 'react-feather';
import CryptoJS from "crypto-js";
import { ApiDetails, KEY } from "../../Constants";

// Import sub-components
import ServiceProviderSection from './ServiceProviderSection';
import ProblemTypeSection from './ProblemTypeSection';
import CategorySection from './CategorySection';
import { handle401Unauthorized, handle403Forbidden } from '../../utils/authErrorHandler';


const ServiceProviderManagement = ({
                                       getLandlordId = null,
                                       showAlert = null,
                                       initialTabValue = 0,
                                       onUpdateComplete = null
                                   }) => {
    const [tabValue, setTabValue] = useState(initialTabValue);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [serviceProviders, setServiceProviders] = useState([]);
    const [problemTypes, setProblemTypes] = useState([]);
    const [categories, setCategories] = useState([]);
const [originalText, setOriginalText] = useState(() => {
    const encryptedData = localStorage.getItem("myMap");
    return encryptedData 
        ? CryptoJS.AES.decrypt(encryptedData, KEY).toString(CryptoJS.enc.Utf8)
        : "";
});;

    // Internal alert state (used if no showAlert prop provided)
    const [internalAlert, setInternalAlert] = useState({ open: false, message: '', severity: 'success' });

    // Get landlord ID from localStorage if not provided through props
    const getEffectiveLandlordId = () => {
        if (getLandlordId) return getLandlordId();

        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            setError("Unable to get landlord ID. Please try again.");
            return null;
        }
    };

    // Display alert message
    const displayAlert = (message, severity = "success") => {
        if (showAlert) {
            showAlert(message, severity);
        } else {
            setInternalAlert({
                open: true,
                message,
                severity
            });

            // Auto-hide after 5 seconds
            setTimeout(() => {
                setInternalAlert(prev => ({ ...prev, open: false }));
            }, 5000);
        }
    };

    // Fetch data on component mount
    useEffect(() => {
        fetchServiceProviders();
        fetchProblemTypes();
        fetchCategories();
    }, []);

    // Set tab value when initialTabValue changes
    useEffect(() => {
        setTabValue(initialTabValue);
    }, [initialTabValue]);

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Fetch service providers
    const fetchServiceProviders = async () => {
        try {
            setLoading(true);
            const landlordId = getEffectiveLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/service-providers", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const data = await response.json();
                setServiceProviders(data);
            } else if (response.status !== 404) {
                throw new Error(`Error: ${response.status}`);
            }

            setError(null);
        } catch (error) {
            console.error("Error fetching service providers:", error);
            setError("Failed to load service providers");
            displayAlert("Failed to load service providers", "error");
        } finally {
            setLoading(false);
        }
    };

    // Fetch problem types
    const fetchProblemTypes = async () => {
        try {
            const landlordId = getEffectiveLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problem-types", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const data = await response.json();
                setProblemTypes(data);
            } else if (response.status !== 404) {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching problem types:", error);
            displayAlert("Failed to load problem types", "error");
        }
    };

    // Fetch categories (problems)
    const fetchCategories = async () => {
        try {
            const landlordId = getEffectiveLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problems/all", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": JSON.parse(originalText)[15][0] !== 'access_token' ? JSON.parse(originalText)[28][1] : JSON.parse(originalText)[15][1]
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                const data = await response.json();
                setCategories(data);
            } else if (response.status !== 404) {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            displayAlert("Failed to load categories", "error");
        }
    };

    // Handle alert close
    const handleAlertClose = () => {
        setInternalAlert({ ...internalAlert, open: false });
    };

    // Handle providers updated
    const handleProvidersUpdated = () => {
        fetchServiceProviders();
        if (onUpdateComplete) onUpdateComplete('serviceProviders');
    };

    // Handle problem types updated
    const handleProblemTypesUpdated = () => {
        fetchProblemTypes();
        if (onUpdateComplete) onUpdateComplete('problemTypes');
    };

    // Handle categories updated
    const handleCategoriesUpdated = () => {
        fetchCategories();
        if (onUpdateComplete) onUpdateComplete('categories');
    };

    return (
        <Box>
            {/* Alert Snackbar (only shown if no external showAlert provided) */}
            {!showAlert && (
                <Snackbar
                    open={internalAlert.open}
                    autoHideDuration={5000}
                    onClose={handleAlertClose}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                    <Alert
                        onClose={handleAlertClose}
                        severity={internalAlert.severity}
                        elevation={6}
                        variant="filled"
                    >
                        {internalAlert.message}
                    </Alert>
                </Snackbar>
            )}

            {/* Main Content */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: '10px',
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    overflow: 'hidden'
                }}
            >
                <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        bgcolor: '#F9FAFB'
                    }}
                >
                    <Tab
                        label="Service Providers"
                        icon={<Settings size={16} />}
                        iconPosition="start"
                        sx={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Problem Types"
                        icon={<Settings size={16} />}
                        iconPosition="start"
                        sx={{ textTransform: 'none' }}
                    />
                    <Tab
                        label="Categories"
                        icon={<Settings size={16} />}
                        iconPosition="start"
                        sx={{ textTransform: 'none' }}
                    />
                </Tabs>

                <Box p={3}>
                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }}>
                            {error}
                        </Alert>
                    )}

                    {loading && tabValue === 0 ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Service Providers Tab */}
                            {tabValue === 0 && (
                                <ServiceProviderSection
                                    providers={serviceProviders}
                                    problemTypes={problemTypes}
                                    getLandlordId={getEffectiveLandlordId}
                                    showAlert={displayAlert}
                                    onProvidersUpdated={handleProvidersUpdated}
                                />
                            )}

                            {/* Problem Types Tab */}
                            {tabValue === 1 && (
                                <ProblemTypeSection
                                    problemTypes={problemTypes}
                                    getLandlordId={getEffectiveLandlordId}
                                    showAlert={displayAlert}
                                    onProblemTypesUpdated={handleProblemTypesUpdated}
                                />
                            )}

                            {/* Categories Tab */}
                            {tabValue === 2 && (
                                <CategorySection
                                    categories={categories}
                                    problemTypes={problemTypes}
                                    getLandlordId={getEffectiveLandlordId}
                                    showAlert={displayAlert}
                                    onCategoriesUpdated={handleCategoriesUpdated}
                                />
                            )}
                        </>
                    )}
                </Box>
            </Paper>
        </Box>
    );
};

export default ServiceProviderManagement;